import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getTheme, useThemeMode } from '../theme';
import { api } from '../api';
import type { QidUser, HuntListing } from '../api';
import type { AiResult, ChatMsg, MeatProduct } from '../types';
import { GREEN_PLACEHOLDER, fmtSEK } from '../components';
import { MapPinIcon } from '../geo/AreaFilter';

// Suppress unused import warnings for identifiers used only inline
void GREEN_PLACEHOLDER;
void fmtSEK;

function animalImg(listing: HuntListing): string {
  if (listing.imageUrl) return listing.imageUrl;
  return GREEN_PLACEHOLDER;
}

function ChatResultCard({ listing, onClick }: { listing: HuntListing; onClick: () => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const img = animalImg(listing);
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
      background: mode === 'light' ? 'rgba(238,143,9,0.06)' : 'rgba(255,255,255,0.06)',
      border: `1px solid ${mode === 'light' ? 'rgba(238,143,9,0.2)' : 'rgba(238,143,9,0.18)'}`,
      borderRadius: 10, padding: '8px 10px', cursor: 'pointer', textAlign: 'left',
      transition: 'all 0.18s', fontFamily: 'inherit',
    }}
    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = mode === 'light' ? 'rgba(238,143,9,0.12)' : 'rgba(238,143,9,0.12)'; (e.currentTarget as HTMLElement).style.borderColor = T.amber }}
    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = mode === 'light' ? 'rgba(238,143,9,0.06)' : 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLElement).style.borderColor = mode === 'light' ? 'rgba(238,143,9,0.2)' : 'rgba(238,143,9,0.18)' }}
    >
      <img src={img} alt={listing.title} style={{ width: 48, height: 48, borderRadius: 7, objectFit: 'cover', flexShrink: 0, border: `1px solid ${T.border}` }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: T.text, fontSize: 13, fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{listing.title}</div>
        <div style={{ color: T.textMuted, fontSize: 11, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{listing.location} · {(listing.gameTypes || []).slice(0, 2).join(', ')}</div>
      </div>
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        <div style={{ color: T.amber, fontSize: 12, fontWeight: 700 }}>{listing.price.toLocaleString('sv')} kr</div>
        <div style={{ color: T.textMuted, fontSize: 10 }}>{listing.spotsAvailable} platser</div>
      </div>
      <span style={{ color: T.amber, fontSize: 14, marginLeft: 2 }}>›</span>
    </button>
  );
}

function ChatMeatCard({ product, onClick }: { product: MeatProduct; onClick: () => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
      background: mode === 'light' ? 'rgba(97,216,54,0.06)' : 'rgba(255,255,255,0.06)',
      border: `1px solid ${mode === 'light' ? 'rgba(97,216,54,0.25)' : 'rgba(97,216,54,0.18)'}`,
      borderRadius: 10, padding: '8px 10px', cursor: 'pointer', textAlign: 'left',
      transition: 'all 0.18s', fontFamily: 'inherit',
    }}
    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = mode === 'light' ? 'rgba(97,216,54,0.12)' : 'rgba(97,216,54,0.14)'; (e.currentTarget as HTMLElement).style.borderColor = '#61D836' }}
    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = mode === 'light' ? 'rgba(97,216,54,0.06)' : 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLElement).style.borderColor = mode === 'light' ? 'rgba(97,216,54,0.25)' : 'rgba(97,216,54,0.18)' }}
    >
      <img src={product.imageUrl} alt={product.species} style={{ width: 48, height: 48, borderRadius: 7, objectFit: 'cover', flexShrink: 0, border: `1px solid ${T.border}` }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: T.text, fontSize: 13, fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{product.species} · {product.cut}</div>
        <div style={{ color: T.textMuted, fontSize: 11, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{product.location} · {(product.weightG / 1000).toFixed(1)} kg · {product.seller}</div>
      </div>
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        <div style={{ color: '#61D836', fontSize: 12, fontWeight: 700 }}>{product.totalPrice} kr</div>
        <div style={{ color: T.textMuted, fontSize: 10 }}>{product.pricePerKg} kr/kg</div>
      </div>
      <span style={{ color: '#61D836', fontSize: 14, marginLeft: 2 }}>›</span>
    </button>
  );
}

export function HeroSearch({ onResult, onClear, onAction, user, onOpenMeat, activeTab, onOpenMap, mapActive }: { onResult: (r: AiResult | null) => void; onClear: () => void; onAction?: (intent: { action: string; params: Record<string, string> }) => void; user?: QidUser | null; onOpenMeat?: (product: MeatProduct) => void; activeTab?: string; onOpenMap?: () => void; mapActive?: boolean }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [q, setQ] = useState('');
  const [ld, setLd] = useState(false);
  const [act, setAct] = useState(false);
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const recognRef = useRef<any>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Panelen under sökfältet: förslag när fältet är tomt, annars chatten
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    if (!panelOpen) return;
    const h = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setPanelOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [panelOpen]);

  // Auto-scroll chat — scroll only within the container, never the page
  useEffect(() => {
    const el = chatContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs]);

  const sendMessage = useCallback(async (override?: string) => {
    const text = (override ?? q).trim();
    if (!text) return;

    // Add user message
    const userMsg: ChatMsg = { id: Date.now().toString(), role: 'user', text, timestamp: Date.now() };
    setMsgs(prev => [...prev, userMsg]);
    setQ(''); setChatOpen(true); setPanelOpen(true); setLd(true);

    try {
      // Try AI chat intent detection first
      const chatRes = await api.aiChat(text, user?.sub || user?.email || undefined);
      const intent = chatRes.intent;

      // If it's a search action, also run the search
      if (intent.action === 'search') {
        try {
          const searchRes = await api.aiSearch(text);
          onResult(searchRes);
          const assistantMsg: ChatMsg = {
            id: (Date.now() + 1).toString(), role: 'assistant',
            text: searchRes.count > 0
              ? `Hittade ${searchRes.count} ${searchRes.count === 1 ? 'jakt' : 'jakter'} — klicka för att öppna:`
              : 'Inga jakter matchade din sökning.',
            intent: { action: 'search', params: { query: text } },
            results: searchRes.results?.slice(0, 5),
            timestamp: Date.now()
          };
          setMsgs(prev => [...prev, assistantMsg]);
        } catch {
          const assistantMsg: ChatMsg = {
            id: (Date.now() + 1).toString(), role: 'assistant',
            text: 'Kunde inte söka just nu, försök igen.',
            timestamp: Date.now()
          };
          setMsgs(prev => [...prev, assistantMsg]);
        }
      } else if (intent.action === 'buy_meat') {
        // Meat search — show results with explanatory text inline
        const meatRes = chatRes.meatResults || [];
        const loc = intent.params.location;
        const species = intent.params.species;
        let introText = '';
        if (meatRes.length > 0) {
          const locPart = loc ? ` i ${loc.charAt(0).toUpperCase() + loc.slice(1)}` : '';
          const speciesPart = species ? ` ${species.toLowerCase()}kött` : 'viltkött';
          introText = `Det finns ${meatRes.length} annonser med${speciesPart}${locPart}. Här är exempel:`;
        } else {
          introText = 'Hittade ingen träff just nu — men här är en del viltkött till salu som kanske passar:';
        }
        const assistantMsg: ChatMsg = {
          id: (Date.now() + 1).toString(), role: 'assistant',
          text: introText,
          intent: { action: 'buy_meat', params: intent.params },
          meatResults: meatRes,
          timestamp: Date.now()
        };
        setMsgs(prev => [...prev, assistantMsg]);
      } else {
        // Action intent — show response and trigger action
        const assistantMsg: ChatMsg = {
          id: (Date.now() + 1).toString(), role: 'assistant',
          text: intent.message,
          intent: { action: intent.action, params: intent.params, suggestions: intent.suggestions },
          timestamp: Date.now()
        };
        setMsgs(prev => [...prev, assistantMsg]);

        // Auto-execute if we have enough params
        if (intent.action !== 'unknown' && !intent.suggestions?.length) {
          setTimeout(() => onAction?.({ action: intent.action, params: intent.params }), 800);
        }
      }
    } catch {
      // Fallback to plain search
      try {
        const r = await api.aiSearch(text);
        onResult(r);
        setMsgs(prev => [...prev, { id: (Date.now() + 1).toString(), role: 'assistant', text: r.message || 'Inga resultat', timestamp: Date.now() }]);
      } catch {
        setMsgs(prev => [...prev, { id: (Date.now() + 1).toString(), role: 'assistant', text: 'Något gick fel. Försök igen.', timestamp: Date.now() }]);
      }
    } finally { setLd(false); }
  }, [q, onResult, onClear, onAction, user]);

  const clearChat = () => { setMsgs([]); setChatOpen(false); setPanelOpen(false); setQ(''); onResult(null); onClear(); };

  // Voice input
  const toggleVoice = useCallback(() => {
    if (listening && recognRef.current) { recognRef.current.stop(); setListening(false); return; }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { return; }
    const r = new SR(); r.lang = 'sv-SE'; r.continuous = false; r.interimResults = true;
    r.onresult = (e: any) => { const t = Array.from(e.results).map((r: any) => r[0].transcript).join(''); setQ(t); };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recognRef.current = r; r.start(); setListening(true);
  }, [listening]);

  const tabChips: Record<string, string[]> = {
    minajakt: [
      'Uppdatera avskjutningsplan',
      'Skriv ut etiketter till kött',
      'Bjud in till jakt',
      'Visa statistik för säsongen',
    ],
    jakt: [
      'Ge mig ett jaktlag med tät dovstamm för smygjakt',
      'Jaktlag med älg i Dalarna',
      'Hitta älgjakt i Sörmland',
      'Drevjakt på vildsvin nära Stockholm',
    ],
    viltkott: [
      'Dovhjortskött nära mig',
      'Älgkött direkt från jägare',
      'Vildsvinskött i bulk',
      'Ekologiskt vilt i Mellansverige',
    ],
    arrende: [
      'Registrera min jaktmark',
      'Sätt rätt pris på min mark',
      'Hitta seriösa jägare',
      'Arrendekontrakt och villkor',
    ],
  };
  const chips = tabChips[activeTab || ''] ?? [
    'Hitta jaktlag i Sörmland',
    'Älgjakt i Dalarna',
    'Viltkött nära mig',
    'Fågeljakt i Norrland',
  ];

  const handleSuggestion = (s: string) => {
    setQ(s);
    sendMessage(s);
  };

  const handleIntentAction = (intent: { action: string; params: Record<string, string> }) => {
    setPanelOpen(false);
    onAction?.(intent);
  };

  const sendSvg = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>';
  const micSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>';

  // Action button label
  const actionLabel = (action: string) => {
    switch (action) {
      case 'start_hunt': return 'Starta jakt';
      case 'create_plan': return 'Öppna avskjutningsplan';
      case 'sell_meat': return 'Gå till Viltkött';
      case 'create_event': return 'Planera jakt';
      case 'open_mark': return 'Öppna jaktmark';
      case 'navigate': return 'Gå dit';
      case 'register_mark': return 'Registrera din mark';
      default: return 'Utför';
    }
  };

  const panelBg = mode === 'light' ? '#ffffff' : T.surface;
  const bubbleBg = mode === 'light' ? '#f4f3ef' : 'rgba(255,255,255,0.06)';

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      {/* ── Sökfält (Qvestiums pill i menyraden) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 44, padding: '0 8px 0 16px', background: T.surface2, border: `1px solid ${act ? T.amber : 'transparent'}`, borderRadius: 999, transition: 'border-color 0.2s' }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } if (e.key === 'Escape') setPanelOpen(false); }}
            onFocus={() => { setAct(true); setPanelOpen(true); }} onBlur={() => setAct(false)}
            placeholder={chatOpen ? 'Skriv ett meddelande...' : 'Sök jakt, jaktlag, viltkött...'}
            aria-label="Sök"
            style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: T.text, fontSize: 14, padding: '8px 0', fontFamily: 'inherit' }} />
        </div>
        <button onClick={toggleVoice} title="Röstinmatning" aria-label="Röstinmatning" style={{ background: listening ? T.amberDim : 'none', border: 'none', color: listening ? T.amber : T.textMuted, padding: 6, cursor: 'pointer', borderRadius: '50%', display: 'flex', flexShrink: 0 }}>
          <span dangerouslySetInnerHTML={{ __html: micSvg }} style={{ display: 'flex', width: 15, height: 15 }} />
        </button>
        {onOpenMap && (
          <button onClick={() => { setPanelOpen(false); onOpenMap(); }} title="Välj område på karta" aria-label="Välj område på karta" style={{ background: mapActive ? T.amber : 'transparent', border: 'none', color: mapActive ? '#fff' : T.textSub, width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MapPinIcon size={15} />
          </button>
        )}
        {q.trim() && (
          <button onClick={() => sendMessage()} disabled={ld} aria-label="Sök" style={{ background: T.amber, border: 'none', color: '#fff', width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {ld ? <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              : <span dangerouslySetInnerHTML={{ __html: sendSvg }} style={{ display: 'flex', width: 13, height: 13 }} />}
          </button>
        )}
      </div>

      {/* ── Panel: förslag eller chatt ── */}
      {panelOpen && (chatOpen || !q) && (
        <div style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, width: 'min(560px, calc(100vw - 24px))', maxHeight: 'min(520px, 70vh)', display: 'flex', flexDirection: 'column', background: panelBg, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.14)', zIndex: 900, overflow: 'hidden' }}>
          {!chatOpen ? (
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Förslag</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {chips.map(c => (
                  <button key={c} onMouseDown={e => e.preventDefault()} onClick={() => handleSuggestion(c)}
                    style={{ background: bubbleBg, border: `1px solid ${T.border}`, color: T.text, borderRadius: 18, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = T.amber; e.currentTarget.style.color = T.amber; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.text; }}
                  >{c}</button>
                ))}
              </div>
            </div>
          ) : (<>
            <div ref={chatContainerRef} style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {msgs.map(m => (
                <div key={m.id} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', animation: 'fadeSlideUp 0.3s ease' }}>
                  <div style={{ maxWidth: '88%', padding: '9px 14px', borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px', background: m.role === 'user' ? T.amber : bubbleBg, color: m.role === 'user' ? '#fff' : T.text, fontSize: 13, lineHeight: 1.5, textAlign: 'left' }}>
                    {m.text}

                    {/* Jakter */}
                    {m.role === 'assistant' && m.results && m.results.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                        {m.results.map(l => (
                          <ChatResultCard key={l.id} listing={l} onClick={() => handleIntentAction({ action: 'open_listing', params: { listingId: l.id } })} />
                        ))}
                      </div>
                    )}

                    {/* Viltkött (öppnas på Qcarne) */}
                    {m.role === 'assistant' && m.meatResults && m.meatResults.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                        {m.meatResults.map(mp => (
                          <ChatMeatCard key={mp.id} product={mp} onClick={() => onOpenMeat?.(mp)} />
                        ))}
                        <button onClick={() => handleIntentAction({ action: 'navigate', params: { tab: 'viltkott' } })} style={{ marginTop: 4, padding: '6px 14px', fontSize: 12, fontWeight: 700, background: 'transparent', border: `1px solid ${T.amber}`, borderRadius: 16, color: T.amber, cursor: 'pointer', fontFamily: 'inherit', alignSelf: 'flex-start' }}>Visa alla annonser →</button>
                      </div>
                    )}

                    {/* Åtgärd */}
                    {m.role === 'assistant' && m.intent && m.intent.action !== 'search' && m.intent.action !== 'unknown' && !m.intent.suggestions?.length && (
                      <button onClick={() => handleIntentAction(m.intent!)} style={{ display: 'block', marginTop: 10, padding: '7px 16px', background: T.amber, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{actionLabel(m.intent.action)}</button>
                    )}

                    {/* Följdförslag */}
                    {m.role === 'assistant' && m.intent?.suggestions && m.intent.suggestions.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                        {m.intent.suggestions.map((sg, i) => (
                          <button key={i} onClick={() => handleSuggestion(sg)} style={{ padding: '5px 12px', fontSize: 12, background: panelBg, border: `1px solid ${T.border}`, borderRadius: 16, color: T.amber, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>{sg}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {ld && <div style={{ display: 'flex', gap: 4, padding: '10px 14px', background: bubbleBg, borderRadius: '14px 14px 14px 4px', alignSelf: 'flex-start' }}>
                {[0, 1, 2].map(i => <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: T.amber, opacity: 0.4, animation: `dotPulse 1.2s ease ${i * 0.2}s infinite` }} />)}
              </div>}
              <div ref={chatEndRef} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderTop: `1px solid ${T.border}` }}>
              {listening ? <span style={{ fontSize: 11, color: T.amber }}>Lyssnar...</span> : <span style={{ fontSize: 11, color: T.textMuted }}>Skriv i sökfältet för att fortsätta</span>}
              <button onClick={clearChat} style={{ padding: '4px 12px', fontSize: 11, color: T.textSub, background: 'none', border: `1px solid ${T.border}`, borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit' }}>Rensa chatt</button>
            </div>
          </>)}
        </div>
      )}
      <style>{`
        @keyframes fadeSlideUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes dotPulse{0%,80%,100%{opacity:0.3;transform:scale(0.8)}40%{opacity:1;transform:scale(1.1)}}
      `}</style>
    </div>
  );
}
